import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Loader2, Undo2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

export function DeletionPendingBanner() {
  const { t } = useTranslation('settings');
  const { user, deletionInfo, signOut, refreshDeletionStatus } = useAuth();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);

  if (!deletionInfo || deletionInfo.status !== 'pending') {
    return null;
  }

  const scheduledDate = deletionInfo.scheduledFor ? new Date(deletionInfo.scheduledFor) : null;
  const isImmediate = scheduledDate && scheduledDate <= new Date();
  const canUndo = scheduledDate && scheduledDate > new Date();

  const handleCancelDeletion = async () => {
    if (!user) return;
    
    setCancelling(true);
    try {
      const { data, error } = await supabase.rpc('cancel_account_deletion', {
        p_user_id: user.id,
      });
      
      if (error) throw error;
      
      if (data) {
        toast.success(t('toast_deletion_cancelled'));
        await refreshDeletionStatus();
        navigate('/dashboard');
      } else {
        toast.error(t('toast_deletion_cancel_expired'));
      }
    } catch (err) {
      console.error('Cancel deletion error:', err);
      toast.error(t('toast_deletion_cancel_failed'));
    } finally {
      setCancelling(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-lg w-full border-destructive/50">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Account Deletion Pending</CardTitle>
          <CardDescription>
            {isImmediate ? (
              'Your account is scheduled for immediate deletion and will be purged shortly.'
            ) : scheduledDate ? (
              <>
                Your account will be permanently deleted on{' '}
                <span className="font-medium text-foreground">
                  {format(scheduledDate, 'MMMM d, yyyy')}
                </span>{' '}
                ({formatDistanceToNow(scheduledDate, { addSuffix: true })}).
              </>
            ) : (
              'Your account deletion is pending.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            While deletion is pending, you cannot access your projects, briefs, or settings. 
            Your client links are also inaccessible.
          </p>

          <div className="flex flex-col gap-3">
            {canUndo && (
              <Button 
                onClick={handleCancelDeletion} 
                disabled={cancelling}
                className="w-full"
              >
                {cancelling ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Undo2 className="mr-2 h-4 w-4" />
                )}
                Undo Deletion & Restore Account
              </Button>
            )}
            
            <Button 
              variant="outline" 
              onClick={handleSignOut}
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>

          {!canUndo && (
            <p className="text-xs text-muted-foreground text-center pt-2">
              The grace period has expired. Your data will be permanently deleted soon.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
